import './consolePoly'

import React, { useContext } from 'react'
import ReactDOM from 'react-dom'
import { applyMiddleware, createStore, compose } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { jt, t } from 'ttag'
import { withScope, captureException } from '@sentry/browser'
import { BrowserRouter } from 'react-router-dom'

import { FixedCallToAction } from '@h2/common/components/fixedCallToAction'
import { Providers } from '@h2/providers'
import { Header } from '@h2/common/components/header'
import { init } from '@h2/init'

import Routes from './Routes'
import root from './reducers'
import setLocale from './localeSetup'
import { setAuthenticationState } from './actions/index'
import handleBadgeChange from './observers'
import { openSigninDialog } from './actions/signinOrRegisterDialog'
import { openContentWizard as openCContentWizard_ } from './actions/contentSnippetWizard'
import { openFileWizardModal as openCFileWizard_ } from './actions/fileManagementWizard'
import { createPlan } from './actions/occupationProfile'
import { openMessageDialog as sendMessageModal_ } from './actions/messageDialog'

import { Globals } from '../../ui/globals'

const composer = DEVELOPMENT
  ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
  : compose
const middlewareSetup = () => composer(applyMiddleware(thunk))
const store = createStore(root, middlewareSetup())
const wrapWithProviders = rootComponentFunc => {
  return (
    <Provider store={store}>
      <Providers>{rootComponentFunc()}</Providers>
    </Provider>
  )
}

store.dispatch(
  setAuthenticationState(
    dynamicValues.userProfile
      ? {
          userDisplayName: dynamicValues.userProfile.userDisplayName,
          ...dynamicValues.userProfile
        }
      : null
  )
)
store.subscribe(handleBadgeChange(store))

window.openSigninDialogSignInTab = () => openSigninDialog('signIn')(store.dispatch)
window.openSigninDialogRegisterTab = () =>
  openSigninDialog('createAccount')(store.dispatch)
window.openContentWizard = () =>
  openCContentWizard_(profileId, profileType)(store.dispatch)
window.openFileWizard = () => openCFileWizard_({ profileId, profileType })(store.dispatch)
window.h2_createOccupationPlan = (occupation, planType) => {
  store.dispatch(createPlan(occupation, planType)).then(p => {
    window.location.href = p.url
  })
}
window.sendMessageModal = (name, displayName) =>
  sendMessageModal_({
    firstName: displayName,
    name
  })(store.dispatch)

const HelpLink = () => {
  const { usePowerschoolHelp } = useContext(Globals)
  const link = (
    <a
      className="bold"
      href={
        usePowerschoolHelp ? '/help/redirect' : '/help/reportissue'
      }>{t`send us a message`}
    </a>
  )

  return jt`If you want to provide us any more information, please ${link}.`
}

class SentryErrorBoundry extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error })
    withScope(scope => {
      scope.setExtras(errorInfo)
      captureException(error)
    })
  }

  render() {
    if (this.state.error) {
      return (
        <div className="container">
          <h2 className="boldcenter">{t`We're sorry — something's gone wrong.`}</h2>
          <p
            style={{
              fontSize: '1.1rem'
            }}>
            <HelpLink />
          </p>
        </div>
      )
    }

    return <div>{this.props.children}</div>
  }
}
const renderMainScreen = () => {
  const localeName = (dynamicValues && dynamicValues.locale) || 'en_US'

  setLocale(localeName).then(() => {
    const headerElement = document.getElementById('h2-header')
    const spaElement = document.getElementById('spatarget')

    /* The Header is wrapped in BrowserRouter to inject the location even though it doesn't need a router */
    if (headerElement && !headerElement?.firstChild) {
      ReactDOM.render(
        wrapWithProviders(() => (
          <SentryErrorBoundry key={window.location.pathname}>
            <BrowserRouter>
              <Header />
            </BrowserRouter>
          </SentryErrorBoundry>
        )),
        headerElement
      )
    }

    if (spaElement) {
      ReactDOM.render(
        wrapWithProviders(() => (
          <SentryErrorBoundry key={window.location.pathname}>
            <Routes />
            <FixedCallToAction />
          </SentryErrorBoundry>
        )),
        spaElement
      )
    }
  })
}

init()
renderMainScreen()

if (module.hot) {
  /**
   * Accept self updates.
   * @see https://webpack.js.org/api/hot-module-replacement/#accept-self
   */
  module.hot.accept(({ message }) => {
    console.log(`Unable to hot reload module: ${message}`)
  })
}

export { store, renderMainScreen }
